import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Link, useLocation } from 'react-router-dom';

import NotificationBell from '@/components/notifications/NotificationBell';
import type { SidebarConfig } from '@/config/sidebarConfig.ts';
import { useAuthStore } from '@/stores/auth-store';
import { getInitials } from '@shared';
import { Globe, LogOut, Menu } from 'lucide-react';

const frontUrl = (import.meta.env.VITE_URL_FRONT as string | undefined)?.replace(/\/$/, '') || 'http://localhost:5232';

interface SidebarProps {
  config: SidebarConfig;
}

export default function Sidebar({ config }: SidebarProps) {
  const [displayMenu, setDisplayMenu] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const { theme, navItems, secondaryItems } = config;
  const user = useAuthStore(s => s.user);
  const displayName = user?.firstName ?? user?.username ?? '';

  const userOrg = user?.organizationsAdmin?.[0]?.organization?.name ?? user?.club?.name ?? theme.userOrg;

  // Close mobile menu on route change — legitimate sync with external nav state
  // eslint-disable-next-line react-hooks/set-state-in-effect
  React.useEffect(() => setDisplayMenu(false), [location.pathname]);

  return (
    <div>
      <div className={'fixed md:hidden z-50'}>
        <Menu
          className={`mt-5 ml-5 font-extrabold w-10 h-10 bg-linear-to-br rounded-md ${theme.userGradientFrom} ${theme.userGradientTo} text-white`}
          onClick={() => setDisplayMenu(!displayMenu)}
        />
      </div>
      <aside
        className={`
    ${displayMenu ? 'fixed' : 'hidden'}
    md:block
    md:fixed
    top-0 left-0 w-70 text-white z-40 overflow-y-auto pt-20 md:pt-0 h-full
  `}
        style={{ background: theme.gradient }}
      >
        {/* User card */}
        <div className={`p-6 border-b ${theme.borderColor}`}>
          <div className={`flex items-center gap-3 rounded-xl ${theme.userCardBg}`}>
            <div
              className={`w-10 h-10 bg-linear-to-br ${theme.userGradientFrom} ${theme.userGradientTo} rounded-full flex items-center justify-center font-bold text-white`}
            >
              {getInitials(user?.firstName, user?.lastName)}
            </div>
            <div className='flex-1 min-w-0'>
              <p className='text-sm font-semibold text-white truncate'>{displayName}</p>
              <p className={`text-xs ${theme.navTextColor} truncate`}>{userOrg}</p>
            </div>
            <div className='flex items-center gap-1'>
              <NotificationBell />
              <button
                type='button'
                onClick={() => {
                  logout();
                  navigate('/login', { replace: true });
                }}
                className={`p-2 rounded-lg ${theme.navTextColor} hover:text-white hover:bg-slate-700 transition-colors cursor-pointer`}
              >
                <LogOut className='w-5 h-5' />
              </button>
            </div>
          </div>
        </div>

        <div className={`flex justify-center border-b ${theme.borderColor} items-center`}>
          <div className={`flex w-fit items-center gap-3 rounded-xl ${theme.userCardBg} text-sm`}>
            <a
              href={frontUrl}
              className={`flex items-center gap-3 py-3 px-4 mx-4 my-1 rounded-xl transition-all ${theme.navTextColor} ${theme.navHoverBg} hover:text-white`}
            >
              <Globe className='w-4 h-4' /> Site BoulesConnect
            </a>
          </div>
        </div>

        {/* Navigation */}
        <nav className='py-4'>
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href + item.label}
                to={item.href}
                className={`flex items-center gap-3 py-3 px-4 mx-4 my-1 rounded-xl transition-all ${
                  isActive
                    ? `${theme.activeColor} text-white`
                    : `${theme.navTextColor} ${theme.navHoverBg} hover:text-white`
                }`}
              >
                <Icon className='w-5 h-5' />
                <span className='text-sm font-medium'>{item.label}</span>
                {item.badge && (
                  <span className={`ml-auto ${theme.badgeBg} text-white text-xs px-2 py-0.5 rounded-full`}>
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}

          <div className={`h-px ${theme.borderColor} my-4 mx-4`} />

          {secondaryItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href + item.label}
                to={item.href}
                className={`flex items-center gap-3 py-3 px-4 mx-4 my-1 rounded-xl transition-all ${
                  isActive
                    ? `${theme.activeColor} text-white`
                    : `${theme.navTextColor} ${theme.navHoverBg} hover:text-white`
                }`}
              >
                <Icon className='w-5 h-5' />
                <span className='text-sm font-medium'>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </aside>
    </div>
  );
}
